import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSites, toClientSite, setActiveSite } from '@/lib/api/sites';
import { getOutlets } from '@/lib/api/outlets';
import { Site } from '@/lib/api/types';
import { toast } from '@/hooks/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { logSite } from '@/lib/breadcrumbs';
import { useAuth } from '@/contexts/auth';
// Storage key for active site
const ACTIVE_SITE_KEY = 'active_site';

export interface SiteContextType {
  activeSite: Site | null;
  sites: Site[];
  isLoading: boolean;
  setActiveSite: (site: Site) => void;
  version: number; // Used to force rerenders of dependent components
  clearCache: () => void; // Utility to clear all site-related cache
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export const SiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeSite, setActiveSiteState] = useState<Site | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const initializedRef = useRef(false);
  const siteChangeInProgressRef = useRef(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, activeCustomer } = useAuth();
  // Reset initialization flag when authentication state changes
  useEffect(() => {
    if (!isAuthenticated) {
      // Reset initialization flag on logout
      initializedRef.current = false;
      setActiveSiteState(null);
      setSites([]);
    }
  }, [isAuthenticated]);

  /**
   * Load sites on mount and initialize active site
   * We use initializedRef to ensure this only runs once on mount
   * AND we make sure authentication is complete before loading sites
   */
  useEffect(() => {
    // Skip if we've already initialized or if not authenticated yet
    // Also wait for user to be available to access user.profile.last_active_site
    if (initializedRef.current || !isAuthenticated || !user) return;

    const loadSites = async () => {
      try {
        setIsLoading(true);
        
        const siteData = await queryClient.fetchQuery({
          queryKey: ['sites'],
          queryFn: ({ signal }) => getSites(signal),
          staleTime: 5 * 60 * 1000,
          retry: 2,
          retryDelay: 1000,
        });
        
        // Check if we have a valid array
        if (!Array.isArray(siteData)) {
          console.error('Invalid sites data received:', siteData);
          setSites([]);
          setIsLoading(false);
          initializedRef.current = true;
          return;
        }
        
        setSites(siteData);
        
        // Check for last_active_site from API first, then localStorage as fallback
        let site = null;
        let selectionSource = '';

        // Check for last_active_site from user profile
        if (user?.profile?.last_active_site) {
          const lastActiveSite = siteData.find(s => s.id === Number(user.profile.last_active_site));
          if (lastActiveSite) {
            site = lastActiveSite;
            selectionSource = 'api_last_active';
          }
        }

        // Fall back to localStorage if no last active site from API
        if (!site) {
          const storedSite = localStorage.getItem(ACTIVE_SITE_KEY);

          if (storedSite) {
            try {
              const parsedSite = JSON.parse(storedSite);
              
              // Verify the stored site exists in the current sites list
              // Compare number IDs for consistency
              const fullSite = siteData.find(s => s.id === Number(parsedSite.id));
              
              if (fullSite) {
                site = fullSite;
                selectionSource = 'localStorage';
              }
            } catch (error) {
              console.error('Failed to parse stored site:', error);
            }
          }
        }
        
        // Auto-select logic: if only one site, use it; if multiple, user must select
        if (!site && !activeSite && siteData.length === 1) {
          // Only one site available - auto-select it
          site = siteData[0];
          selectionSource = 'auto_selected_single';
          console.log('Auto-selecting single available site:', site.name);
        } else if (!site && !activeSite && siteData.length > 1) {
          // Multiple sites available but none selected - will need user selection
          console.log('Multiple sites available, user selection required');
        }

        if (site && !activeSite) {
          // Set the state directly (not through the handler)
          setActiveSiteState(site);
          localStorage.setItem(ACTIVE_SITE_KEY, JSON.stringify(toClientSite(site)));
          
          // Log the auto-selection
          logSite(`Auto-selected site from ${selectionSource}`, { 
            siteId: site.id,
            siteName: site.name 
          });
          
          // Preload outlets data in the background for better UX
          queryClient.prefetchQuery({
            queryKey: ['outlets'],
            queryFn: ({ signal }) => getOutlets(undefined, signal),
            staleTime: 5 * 60 * 1000, // 5 minutes
          }).catch(error => {
            console.warn('Failed to preload outlets data:', error);
          });
        }

        // Mark as initialized to avoid re-runs
        initializedRef.current = true;
      } catch (error) {
        console.error('Error loading sites:', error);
        // Initialize an empty sites array on error to avoid undefined
        setSites([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    // Only attempt to load sites if we're authenticated and user is available
    if (isAuthenticated && user) {
      loadSites();
    }
  }, [queryClient, isAuthenticated, user]); // Include isAuthenticated and user in the dependency array

  // Listen for customer changes and re-initialize site selection
  useEffect(() => {
    const handleCustomerChange = () => {
      // Reset initialization flag to allow re-initialization
      initializedRef.current = false;
      // Clear current active site to force re-selection
      setActiveSiteState(null);
      // Trigger site loading again
      if (isAuthenticated) {
        const loadSites = async () => {
          try {
            setIsLoading(true);
            
            const siteData = await queryClient.fetchQuery({
              queryKey: ['sites'],
              queryFn: ({ signal }) => getSites(signal),
              staleTime: 5 * 60 * 1000,
              retry: 2,
              retryDelay: 1000,
            });
            
            if (Array.isArray(siteData) && siteData.length > 0) {
              setSites(siteData);
              
              // Try to use last_active_site from API, fall back to first available
              let selectedSite = siteData[0];
              let selectionSource = 'first_available';
              
              if (user?.profile?.last_active_site) {
                const lastActiveSite = siteData.find(s => s.id === Number(user.profile.last_active_site));
                if (lastActiveSite) {
                  selectedSite = lastActiveSite;
                  selectionSource = 'api_last_active';
                }
              }
              
              setActiveSiteState(selectedSite);
              localStorage.setItem(ACTIVE_SITE_KEY, JSON.stringify(toClientSite(selectedSite)));
              
              logSite(`Auto-selected site after customer change (${selectionSource})`, { 
                siteId: selectedSite.id,
                siteName: selectedSite.name 
              });
            }
          } catch (error) {
            console.error('[SiteContext] Error re-loading sites after customer change:', error);
          } finally {
            setIsLoading(false);
          }
        };
        
        loadSites();
      }
    };

    // Add event listener for customer changes
    window.addEventListener('customer-changed', handleCustomerChange);

    // Cleanup event listener
    return () => {
      window.removeEventListener('customer-changed', handleCustomerChange);
    };
  }, [queryClient, isAuthenticated]);

  /**
   * Enhanced utility to clear all site-related caches
   * 
   * This function uses a structured approach to ensure all site-related
   * data is properly invalidated in the React Query cache.
   */
  const clearCache = useCallback(() => {
    // 1. Define resource categories for better organization
    const resourceCategories = {
      // Core resources that are directly tied to sites
      core: ['sites', 'site-stats', 'dashboard-stats'],
      
      // Location hierarchy resources
      locations: ['addresses', 'floors', 'rooms', 'doors'],
      
      // Network related resources
      networks: ['networks', 'network-stats', 'speed-limits', 'wireless', 'outlets'],
      
      // User and tenant related resources
      entities: ['tenants', 'tenant-occupancy', 'users']
    };
    
    // 2. Invalidate all resource categories
    Object.values(resourceCategories).flat().forEach(resource => {
      // First invalidate the base query
      queryClient.invalidateQueries({ queryKey: [resource] });
      
      // Then more aggressively remove the queries to force refetch
      // This ensures that when switching sites, old data isn't shown
      queryClient.removeQueries({ queryKey: [resource] });
      
      // For site-specific stats, also remove them with the site ID if we have an active site
      if (activeSite?.id && resource.includes('stats')) {
        queryClient.removeQueries({ queryKey: [resource, activeSite.id] });
      }
    });

    // 3. Handle compound queries where site_id is in a filter object
    queryClient.invalidateQueries({
      predicate: (query) => {
        return query.queryKey.some(key => 
          typeof key === 'object' && 
          key !== null && 
          (
            'site_id' in key || 
            'address_id' in key || 
            'floor_id' in key ||
            // Include other filter keys that might be indirectly related to site context
            'tenant_id' in key ||
            'network_id' in key
          )
        );
      }
    });

    // 4. Handle queries that contain nested site data
    if (activeSite?.id) {
      const siteId = activeSite.id;
      
      // Invalidate specific resources with site ID in key
      [
        ['site-stats', siteId],
        ['addresses'],
        ['floors'],
        ['rooms'],
        ['doors'],
        ['tenants'],
        ['networks'],
        ['wireless'],
      ].forEach(key => {
        queryClient.invalidateQueries({ queryKey: key });
        queryClient.removeQueries({ queryKey: key });
      });
    }

    // UniFi RF data is site-scoped via X-Site-ID header; invalidate so it refetches on site change
    queryClient.invalidateQueries({ queryKey: ['unifi', 'rf'] });

  }, [queryClient, activeSite?.id]);

  /**
   * Handles active site selection with improved reliability
   * 
   * This function:
   * 1. Updates the active site in state
   * 2. Persists the selection to localStorage
   * 3. Shows a notification to the user
   * 4. Logs the site change
   * 5. Forces refetch of all site-dependent queries
   * 6. Increments the version counter to force re-renders
   * 
   * @param site - The site to set as active
   */
  const handleSetActiveSite = useCallback(async (site: Site) => {
    if (activeSite?.id === site.id) {
      console.log('Site is already active, not updating');
      return;
    }
    // Prevent multiple simultaneous site changes
    if (siteChangeInProgressRef.current) {
      console.log('Site change already in progress, ignoring');
      return;
    }
    
    // Set flag to prevent multiple changes
    siteChangeInProgressRef.current = true;
    
    try {
      // Debug log to help troubleshoot
      console.log(`Setting active site: ${site.name} (${site.id})`);
      
      // Step 1: Set as active in API first
      await setActiveSite(site.id.toString());
      
      // Show toast notification about site change
      toast({
        title: "Site changed",
        description: `Active site: ${site.name}`,
        duration: 2000,
      });
      
      // Step 2: First cancel any ongoing queries
      queryClient.cancelQueries();
      
      // Step 3: Clear all relevant caches
      clearCache();

      // Step 4: Update state atomically to prevent race conditions
      setActiveSiteState({ ...site });
      setVersion(v => v + 1);
      
      // Step 5: Store in localStorage
      localStorage.setItem(ACTIVE_SITE_KEY, JSON.stringify(toClientSite(site)));
      
      // Step 6: Log the site change
      logSite("Manual site selection", { 
        siteId: site.id,
        siteName: site.name 
      });
    
    } catch (error) {
      console.error('[SiteContext] Error setting active site:', error);
      toast({
        title: "Error changing site",
        description: "Failed to change site. Please try again.",
        duration: 3000,
      });
    } finally {
      // Reset the in-progress flag
      siteChangeInProgressRef.current = false;
    }
    
    // Step 7: Use a short delay to ensure React has updated components
    setTimeout(() => {
      // Get high-priority data queries that need immediate refresh
      const highPriorityQueries = [
        'site-stats',
        'dashboard-stats',
        'addresses', 
        'rooms',
        'outlets'
      ];

      // Invalidate and immediately refetch high-priority data
      highPriorityQueries.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] });
        queryClient.refetchQueries({ queryKey: [key] });
      });
      
      // Specifically refetch the new site's stats
      queryClient.invalidateQueries({ queryKey: ['site-stats', site.id] });
      
      // Preload outlets data for the new site
      queryClient.prefetchQuery({
        queryKey: ['outlets'],
        queryFn: ({ signal }) => getOutlets(undefined, signal),
        staleTime: 5 * 60 * 1000, // 5 minutes
      }).catch(error => {
        console.warn('Failed to preload outlets data for new site:', error);
      });
      queryClient.refetchQueries({ queryKey: ['site-stats', site.id] });
      
      // Special handling for the Dashboard page - ensure it gets completely fresh data
      if (location.pathname === '/dashboard') {
        // Remove (not just invalidate) all dashboard related queries
        queryClient.removeQueries({ queryKey: ['site-stats'] });
        queryClient.removeQueries({ queryKey: ['dashboard-stats'] });
        console.log('On dashboard, removed all stats queries for fresh data');
      }
      
      // Handle navigation for site-specific pages
      if (location.pathname.match(/\/site\/\d+/)) {
        navigate('/site');
      }
      
      // Debug log to confirm new state
    }, 50); // Short delay to ensure state updates have propagated
    
  }, [queryClient, navigate, location.pathname, version, clearCache]);

  const value = {
    activeSite,
    sites,
    isLoading,
    setActiveSite: handleSetActiveSite,
    version,
    clearCache,
  };

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

/**
 * Custom hook to use the site context
 * @returns The site context value
 */
export const useSiteContext = () => {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSiteContext must be used within a SiteProvider');
  }
  return context;
};

export default SiteContext;